import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { parseMoveBpaCsv, type ParsedMoveBpaCsv } from "./parseMoveBpaCsv.js";
import { tally, useMoveBpaBatch, type MoveBpaTotals } from "./useMoveBpaBatch.js";

export type MoveBpaFile = { name: string; parsed: ParsedMoveBpaCsv };

export type MoveBlock =
  | { kind: "no_dry_run" }
  | { kind: "invalid_rows"; count: number }
  | { kind: "failed_rows"; count: number };

type DryRunCheck = { file: MoveBpaFile; totals: MoveBpaTotals };

type MoveBpaBatchApi = ReturnType<typeof useMoveBpaBatch>;

type MoveBpaRunState = Omit<MoveBpaBatchApi, "start" | "reset">;

type MoveBpaRunContextValue = MoveBpaRunState & {
  file: MoveBpaFile | null;
  parsing: boolean;
  parseError: string | null;
  selectFile: (selected: File) => Promise<void>;
  dryRunTotals: MoveBpaTotals | null;
  moveBlock: MoveBlock | null;
  startDryRun: () => Promise<void>;
  startMove: () => Promise<void>;
  sectionMounted: boolean;
  registerSection: () => () => void;
};

const MoveBpaRunContext = createContext<MoveBpaRunContextValue | null>(null);

function runState({ start, reset, ...state }: MoveBpaBatchApi): MoveBpaRunState {
  return state;
}

export function MoveBpaRunProvider({ children }: { children: ReactNode }) {
  const batch = useMoveBpaBatch();
  const { start, reset } = batch;
  const [file, setFile] = useState<MoveBpaFile | null>(null);
  const [parsing, setParsing] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);
  const [dryRunCheck, setDryRunCheck] = useState<DryRunCheck | null>(null);
  const [mountedSections, setMountedSections] = useState(0);
  const busyRef = useRef(false);

  const moveBlock = useMemo((): MoveBlock | null => {
    if (!file) return { kind: "no_dry_run" };
    const invalid = file.parsed.invalid.length;
    if (invalid > 0) return { kind: "invalid_rows", count: invalid };
    if (!dryRunCheck || dryRunCheck.file !== file) return { kind: "no_dry_run" };
    const failed = dryRunCheck.totals.failed;
    return failed > 0 ? { kind: "failed_rows", count: failed } : null;
  }, [file, dryRunCheck]);
  const moveBlockRef = useRef(moveBlock);
  moveBlockRef.current = moveBlock;

  const exclusive = useCallback(async (task: () => Promise<void>) => {
    if (busyRef.current) return;
    busyRef.current = true;
    try {
      await task();
    } finally {
      busyRef.current = false;
    }
  }, []);

  const selectFile = useCallback(
    async (selected: File) => {
      if (busyRef.current) return;
      reset();
      setParsing(true);
      setParseError(null);
      setFile(null);
      setDryRunCheck(null);
      try {
        setFile({ name: selected.name, parsed: await parseMoveBpaCsv(selected) });
      } catch (err) {
        setParseError(err instanceof Error ? err.message : "Failed to parse CSV");
      } finally {
        setParsing(false);
      }
    },
    [reset],
  );

  const startDryRun = useCallback(
    () =>
      exclusive(async () => {
        if (!file) return;
        setDryRunCheck(null);
        const summary = await start(file.parsed.valid, true);
        if (!summary.finished) return;
        setDryRunCheck({ file, totals: tally(summary.results) });
      }),
    [exclusive, start, file],
  );

  const startMove = useCallback(
    () =>
      exclusive(async () => {
        if (moveBlockRef.current !== null || !file) return;
        setDryRunCheck(null);
        await start(file.parsed.valid, false);
      }),
    [exclusive, start, file],
  );

  const registerSection = useCallback(() => {
    setMountedSections(count => count + 1);
    return () => setMountedSections(count => count - 1);
  }, []);

  const dryRunTotals = dryRunCheck && dryRunCheck.file === file ? dryRunCheck.totals : null;

  const value = useMemo(
    (): MoveBpaRunContextValue => ({
      ...runState(batch),
      file,
      parsing,
      parseError,
      selectFile,
      dryRunTotals,
      moveBlock,
      startDryRun,
      startMove,
      sectionMounted: mountedSections > 0,
      registerSection,
    }),
    [
      batch,
      file,
      parsing,
      parseError,
      selectFile,
      dryRunTotals,
      moveBlock,
      startDryRun,
      startMove,
      mountedSections,
      registerSection,
    ],
  );

  return <MoveBpaRunContext.Provider value={value}>{children}</MoveBpaRunContext.Provider>;
}

export function useMoveBpaRun(): MoveBpaRunContextValue {
  const value = useContext(MoveBpaRunContext);
  if (!value) throw new Error("useMoveBpaRun must be used inside MoveBpaRunProvider");
  return value;
}

export function useOptionalMoveBpaRun(): MoveBpaRunContextValue | null {
  return useContext(MoveBpaRunContext);
}
